import { useState, useMemo, useEffect, memo } from "react";
import { format } from "date-fns";
import { Namespace, useDateFnsLocale, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import {
  Button,
  Card,
  cn,
  PlateNumber,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@racoon/ui";
import { Plus } from "lucide-react";
import { fromTimestamp } from "@racoon/lib";
import { IcCancel, IcJourney, IcPencil, IcPetrol, IcPrint, IcSettings } from "@racoon/icons";
import { TruckImage } from "~/shared/ui/truck-image";
import { shiftNumber } from "~/shared/lib/shift-number";
import {
  formatUsage,
  humanTruckBadge,
  humanTruckType,
  isShiftRunning,
  isShiftTerminal,
  NO_DATA,
  shiftStatusTone,
} from "../helpers";
import { isTruckArchived } from "~/shared/lib/truck-type";
import { TruckModal } from "./truck.modal";
import { CreateShiftModal } from "./CreateShiftModal";
import { isTtnApproved } from "~/shared/lib/shift-status";
import { ShiftStatusBadge } from "./ShiftStatusBadge";
import type { ShiftId } from "../hooks/useShiftApproval";

// Tab order: finished, then running, then planned — a day read left to right. Every
// on-shift status (IDLE, ENROUTE, ON_SERVICE...) ranks as running, matching how
// preferredShiftIndex groups them. Tabs are labelled by position, so they always count
// 1, 2, 3 across the strip; a shift's number therefore changes when a status change
// moves its tab.
const shiftStatusRank = (status: ConnectProto.DriverOnShiftStatus) => {
  if (isShiftTerminal(status)) return 0;
  if (status === ConnectProto.DriverOnShiftStatus.NOT_APPROVED) return 2;
  return 1;
};

interface TruckCardProps {
  truckStatus: ConnectProto.CurrentTruckStatus & { shifts?: ConnectProto.DriverOnShift[] };
  /** Latest odometer/fuel snapshot for this truck; absent row = no data at all. */
  readings?: ConnectProto.TruckLatestReadingsRow;
  available_drivers: ConnectProto.User[];
  /** Per shift, not per screen — one shift starting must not disable the rest. */
  is_completing_shift: (shift_id: ShiftId) => boolean;
  /** Any action on this shift is in flight — what gates the buttons. */
  is_shift_busy: (shift_id: ShiftId) => boolean;
  on_print_shift: (shift: ConnectProto.DriverOnShift, truck: ConnectProto.Truck) => void;
  on_complete_shift: (shift_id: ShiftId) => void;
  on_cancel_shift: (shift_id: ShiftId) => void;
  on_approved_shift: (shift_id: ShiftId) => void;
  defaultDate?: Date;
}

export const TruckCard = memo<TruckCardProps>(
  ({
    truckStatus,
    readings,
    available_drivers,
    is_completing_shift,
    is_shift_busy,
    on_print_shift,
    on_complete_shift,
    on_cancel_shift,
    on_approved_shift,
    defaultDate,
  }) => {
    const t = useT();
    const [activeTab, setActiveTab] = useState("0");
    const locale = useDateFnsLocale();

    const shifts = useMemo(() => {
      return [...(truckStatus.shifts || [])].sort(
        (a, b) =>
          shiftStatusRank(a.status) - shiftStatusRank(b.status) ||
          (a.id === b.id ? 0 : a.id < b.id ? -1 : 1),
      );
    }, [truckStatus]);

    useEffect(() => {
      const index = parseInt(activeTab, 10);
      if (isNaN(index) || index >= shifts.length) {
        setActiveTab("0");
      }
    }, [shifts.length, activeTab]);

    const activeShift = useMemo(() => {
      const index = parseInt(activeTab, 10);
      return shifts[index];
    }, [activeTab, shifts]);

    const preferredShiftIndex = useMemo(() => {
      if (shifts.length === 0) return -1;

      const activeIndex = shifts.findIndex((shift) => isShiftRunning(shift.status));
      if (activeIndex >= 0) return activeIndex;

      const plannedIndex = shifts.findIndex(
        (shift) => shift.status === ConnectProto.DriverOnShiftStatus.NOT_APPROVED,
      );
      if (plannedIndex >= 0) return plannedIndex;

      const closedIndex = shifts.findIndex((shift) => isShiftTerminal(shift.status));
      if (closedIndex >= 0) return closedIndex;

      return 0;
    }, [shifts]);

    const shiftsSignature = useMemo(
      () => shifts.map((shift) => `${shift.id?.toString() || "0"}:${shift.status}`).join("|"),
      [shifts],
    );

    useEffect(() => {
      if (preferredShiftIndex < 0) {
        setActiveTab("0");
        return;
      }

      setActiveTab(String(preferredShiftIndex));
    }, [preferredShiftIndex, shiftsSignature]);

    const isTerminal = useMemo(() => {
      return !!activeShift && isShiftTerminal(activeShift.status);
    }, [activeShift]);

    const hasShift = useMemo(() => {
      return !!activeShift && !isShiftTerminal(activeShift.status);
    }, [activeShift]);

    const isPlanned = useMemo(() => {
      return hasShift && activeShift?.status === ConnectProto.DriverOnShiftStatus.NOT_APPROVED;
    }, [hasShift, activeShift]);

    const hasShiftsOfDay = useMemo(() => {
      return shifts.length > 0;
    }, [shifts]);

    const isTruckActive = useMemo(() => {
      return shifts.some((s) => !isShiftTerminal(s.status));
    }, [shifts]);

    // Offered on every non-terminal shift; the backend decides what it can
    // actually cancel and its rejection is surfaced to the dispatcher.
    const isCancellable = hasShift;

    /**
     * The status row shows the best known window, each half marked fact or not
     * (non-fact halves render muted): fact `startedAt`/`completedAt` once they
     * exist, the planned `twStartAt`/`twEndAt` until then.
     */
    const shiftWindow = useMemo(() => {
      if (!activeShift) return null;
      const started = fromTimestamp(activeShift.startedAt);
      const completed = fromTimestamp(activeShift.completedAt);
      const twStart = fromTimestamp(activeShift.twStartAt);
      const twEnd = fromTimestamp(activeShift.twEndAt);

      const start = started
        ? { date: started, isFact: true }
        : twStart
          ? { date: twStart, isFact: false }
          : null;

      const end = completed
        ? { date: completed, isFact: true }
        : twEnd
          ? { date: twEnd, isFact: false }
          : null;

      return { start, end };
    }, [activeShift]);

    /** Fact times: shown once the shift is over, per the card design. */
    const factPeriodLabel = useMemo(() => {
      if (!activeShift || !isShiftTerminal(activeShift.status)) return "";
      const startDate = fromTimestamp(activeShift.startedAt);
      if (!startDate) return "";

      const formattedStart = format(startDate, "dd MMM yyyy HH:mm", { locale });
      const completedDate = fromTimestamp(activeShift.completedAt);
      return completedDate
        ? `${formattedStart} - ${format(completedDate, "dd MMM yyyy HH:mm", { locale })}`
        : formattedStart;
    }, [activeShift, locale]);

    // The odometer total and today's distance come from different sources — the
    // tracker's counter and the GPS track — so they are shown side by side, never
    // subtracted. Rounded as shown; an unset or zero reading is shown as no data.
    const mileageKm = Math.round(readings?.mileageKm ?? 0);
    const mileageTodayKm = Math.round(readings?.mileageTodayKm ?? 0);
    const fuelLitres = Math.round(readings?.fuelLitres ?? 0);
    const noData = t(Namespace.Common, "noData");

    // An archived truck takes no new shifts, so the control is shown disabled
    // rather than hidden — the slot keeps the divider on every card aligned.
    const isArchived = isTruckArchived(truckStatus.truck);

    const planShiftTrigger = (
      <button
        type="button"
        data-testid="truck-card-plan-shift"
        disabled={isArchived}
        aria-label={t(Namespace.Autopark, "cards.planShift")}
        title={t(Namespace.Autopark, "cards.planShift")}
        className="mb-1.5 ml-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-blue text-white transition-colors hover:bg-brand-blue/90 disabled:cursor-not-allowed disabled:bg-text-secondary/30 disabled:hover:bg-text-secondary/30"
      >
        <Plus aria-hidden className="h-2.5 w-2.5" strokeWidth={3} />
      </button>
    );

    const planShiftButton = isArchived ? (
      planShiftTrigger
    ) : (
      <CreateShiftModal
        truckId={String(truckStatus.truck?.id)}
        trucks={truckStatus.truck ? [truckStatus.truck] : []}
        drivers={available_drivers.map((driver) => ({
          id: String(driver.id),
          name: `${driver.firstName || ""} ${driver.lastName || ""}`.trim(),
        }))}
        defaultDate={defaultDate}
        trigger={planShiftTrigger}
      />
    );

    return (
      <Card
        data-testid="truck-card"
        className="hover:shadow transition flex flex-col w-full aspect-square overflow-hidden"
      >
        {/* Header + body; the body soaks up the free height so the tab/divider
            line sits at the same level on every card, whatever its state. */}
        <div className="flex flex-col flex-1 min-h-0">
          <div
            data-testid="truck-card-header"
            className="flex items-center justify-between px-4 py-5"
          >
            <div className="flex items-center gap-3.5">
              <PlateNumber plateNumber={truckStatus.truck?.plateNumber || ""} />
              <TruckModal
                truck={truckStatus.truck}
                shifts={shifts}
                trigger={<IcSettings className="w-5 h-5 text-text-secondary cursor-pointer" />}
              />
            </div>
            {humanTruckBadge(truckStatus.truck?.status, isTruckActive, t)}
          </div>
          <Separator />
          <div className="flex flex-1 min-h-0 w-full gap-4 px-4">
            {/* Equal gaps above the title, between the two groups and above the tabs,
                so the readings sit midway between the type line and the tab strip. */}
            <div className="flex flex-1 min-w-0 flex-col justify-evenly">
              <div data-testid="truck-card-specs" className="flex flex-col gap-1">
                {/* One line each, truncated — a long model name must not change the card's height. */}
                <div className="text-sm text-start w-full font-semibold text-text-primary truncate">
                  {[
                    truckStatus.truck?.truckType?.manufacturer,
                    truckStatus.truck?.truckType?.model || truckStatus.truck?.model,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                </div>
                <div className="text-start font-normal text-sm w-full text-text-secondary truncate">
                  {humanTruckType(truckStatus.truck?.truckType?.type || truckStatus.truck?.type, t)}
                </div>
                {truckStatus.truck?.techPassport ? (
                  <div className="text-start font-normal text-sm w-full text-text-secondary">
                    {t(Namespace.Autopark, "modals.truckModal.techPassport.label")}{" "}
                    {truckStatus.truck.techPassport}
                  </div>
                ) : null}
              </div>
              <div data-testid="truck-card-readings" className="flex flex-col gap-1.5">
                <div className="flex items-center gap-1 text-sm w-full">
                  <IcJourney className="w-4 h-4 shrink-0 text-brand-blue" />
                  <span data-testid="truck-card-mileage" className="text-text-secondary">
                    {mileageKm || mileageTodayKm ? (
                      <>
                        {mileageKm ? mileageKm.toLocaleString() : null}
                        {mileageTodayKm ? (
                          <span className="text-brand-green">
                            {mileageKm ? " + " : null}
                            {mileageTodayKm.toLocaleString()}
                          </span>
                        ) : null}{" "}
                        {t(Namespace.Autopark, "units.km")}
                      </>
                    ) : (
                      noData
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-sm w-full">
                  <IcPetrol className="w-4 h-4 shrink-0 text-brand-blue" />
                  <span data-testid="truck-card-fuel" className="text-text-secondary">
                    {fuelLitres
                      ? formatUsage(fuelLitres, t(Namespace.Autopark, "units.liters"))
                      : noData}
                  </span>
                </div>
              </div>
            </div>
            {/* Full body height rather than a fixed 160px, so on a narrow card the
                picture shrinks instead of pushing the readings into the tabs. */}
            <div className="flex w-40 shrink-0 items-center justify-center py-2">
              <TruckImage truck={truckStatus.truck} grayscale={!isTruckActive} />
            </div>
          </div>
        </div>

        {/* Bottom zone. With shifts: tab strip, a constant-height content area,
            footer. Without shifts there is nothing to show between the divider
            and the footer, so the divider sits just above «Запланировать смену». */}
        <div className="flex flex-col pb-4">
          {!hasShiftsOfDay ? (
            // No shifts yet: the strip carries only the plan-shift control, so the
            // divider still lands just above the footer.
            <div className="flex h-9 w-full items-end border-b border-border-primary px-4">
              {planShiftButton}
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList data-testid="truck-card-shift-tabs" variant="folder" className="px-4">
                {/* pb-px/-mb-px keep the active tab's 1px notch over the list border
                    inside this scroll container instead of it being clipped off. */}
                <div className="flex min-w-0 items-end overflow-x-auto hidden-scrollbar pb-px -mb-px">
                  {shifts.map((shift, index) => (
                    <TabsTrigger
                      key={index}
                      data-testid="truck-card-shift-tab"
                      variant="folder"
                      value={String(index)}
                      className="shrink-0 justify-center gap-2"
                    >
                      <span
                        className={cn(
                          "h-2 w-2 min-h-2 min-w-2 shrink-0 rounded-full",
                          shiftStatusTone(shift.status).dot,
                        )}
                      />
                      {t(Namespace.Autopark, "cards.shift")} {shiftNumber(shift, shifts)}
                    </TabsTrigger>
                  ))}
                </div>
                {planShiftButton}
              </TabsList>
              {shifts.map((_shift, index) => (
                <TabsContent
                  key={index}
                  value={String(index)}
                  className="px-4 min-h-[48px] !mt-6 mb-2"
                >
                  {activeShift ? (
                    <div className="flex flex-col gap-2">
                      <div
                        data-testid="truck-card-driver"
                        className="flex items-center justify-between gap-1"
                      >
                        <div className="text-xs text-text-secondary">
                          {t(Namespace.Autopark, "cards.driver")}
                        </div>
                        <div className="text-xs font-medium">
                          {activeShift.driver
                            ? `${activeShift.driver.firstName || ""} ${activeShift.driver.lastName || ""}`.trim()
                            : t(Namespace.Autopark, "cards.notAssigned")}
                        </div>
                      </div>
                      <div
                        data-testid="truck-card-status"
                        className="flex items-center justify-between gap-1"
                      >
                        <div className="text-xs text-text-secondary">
                          {t(Namespace.Autopark, "table.status")}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs whitespace-nowrap">
                            <span
                              className={shiftWindow?.start?.isFact ? "" : "text-text-secondary"}
                            >
                              {shiftWindow?.start
                                ? format(shiftWindow.start.date, "HH:mm")
                                : NO_DATA}
                            </span>
                            {" - "}
                            <span className={shiftWindow?.end?.isFact ? "" : "text-text-secondary"}>
                              {shiftWindow?.end ? format(shiftWindow.end.date, "HH:mm") : NO_DATA}
                            </span>
                          </span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                {/* flex: no inline line box, so the row is exactly the pill's height. */}
                                <span className="flex">
                                  <ShiftStatusBadge
                                    status={activeShift.status}
                                    ttnApproved={isTtnApproved(activeShift)}
                                  />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p>
                                  {t(Namespace.Autopark, "cards.shiftId")} S
                                  {activeShift.id?.toString()}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-text-secondary">
                      {t(Namespace.Autopark, "cards.noShiftPlanned")}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          )}

          <div
            data-testid="truck-card-footer"
            className="flex items-center justify-between gap-2 mt-4 px-4"
          >
            <div className="flex items-center gap-2">
              {isPlanned ? (
                activeShift.driver?.id ? (
                  <Button
                    data-testid="truck-card-start-shift"
                    size="sm"
                    className="!bg-brand-green !text-white"
                    disabled={is_shift_busy(activeShift.id)}
                    onClick={() => on_approved_shift(activeShift.id)}
                  >
                    {t(Namespace.Autopark, "cards.confirmShift")}
                  </Button>
                ) : (
                  <CreateShiftModal
                    truckId={String(truckStatus.truck?.id)}
                    trucks={truckStatus.truck ? [truckStatus.truck] : []}
                    drivers={[
                      ...(activeShift.driver
                        ? [
                            {
                              id: String(activeShift.driver.id),
                              name: `${activeShift.driver.firstName || ""} ${activeShift.driver.lastName || ""}`.trim(),
                            },
                          ]
                        : []),
                      ...available_drivers.map((driver) => ({
                        id: String(driver.id),
                        name: `${driver.firstName || ""} ${driver.lastName || ""}`.trim(),
                      })),
                    ]}
                    activeDriverId={
                      activeShift.driver?.id ? String(activeShift.driver.id) : undefined
                    }
                    activeShiftId={activeShift.id}
                    activeShiftStatus={activeShift.status}
                    planStart={activeShift.twStartAt || activeShift.createdAt}
                    defaultDate={defaultDate}
                    trigger={
                      <Button
                        id={`truck-tab-assign-driver-select-${truckStatus.truck?.id}`}
                        variant="opacityBlueWithoutOutline"
                        size="sm"
                      >
                        {t(Namespace.Autopark, "grid.card.noDriver")}
                      </Button>
                    }
                  />
                )
              ) : hasShift ? (
                <Button
                  data-testid="truck-card-complete-shift"
                  size="sm"
                  disabled={is_shift_busy(activeShift.id)}
                  onClick={() => on_complete_shift(activeShift.id)}
                >
                  {is_completing_shift(activeShift.id)
                    ? t(Namespace.Autopark, "cards.completing")
                    : t(Namespace.Autopark, "cards.endShift")}
                </Button>
              ) : activeShift && isTerminal ? (
                factPeriodLabel && (
                  <div className="text-xs text-text-secondary">{factPeriodLabel}</div>
                )
              ) : null}
              {isCancellable && (
                <button
                  type="button"
                  data-testid="truck-card-cancel-shift"
                  aria-label={t(Namespace.Autopark, "cards.cancelShift")}
                  disabled={is_shift_busy(activeShift.id)}
                  className="shrink-0 rounded-full text-brand-red transition hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:opacity-50"
                  onClick={() => activeShift.id && on_cancel_shift(activeShift.id)}
                >
                  <IcCancel aria-hidden className="h-6 w-6" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 justify-end min-w-0 h-8">
              {/* Each icon sits in a 32px slot, as on the design. */}
              <div className="flex items-center gap-1 shrink-0">
                {activeShift &&
                  (isTerminal ? (
                    <IcPencil
                      data-testid="truck-card-edit-shift"
                      className="box-content p-2 w-4 h-4 text-text-secondary/40 cursor-not-allowed"
                    />
                  ) : (
                    <CreateShiftModal
                      truckId={String(truckStatus.truck?.id)}
                      trucks={truckStatus.truck ? [truckStatus.truck] : []}
                      drivers={[
                        ...(activeShift.driver
                          ? [
                              {
                                id: String(activeShift.driver.id),
                                name: `${activeShift.driver.firstName || ""} ${activeShift.driver.lastName || ""}`.trim(),
                              },
                            ]
                          : []),
                        ...available_drivers.map((driver) => ({
                          id: String(driver.id),
                          name: `${driver.firstName || ""} ${driver.lastName || ""}`.trim(),
                        })),
                      ]}
                      activeDriverId={
                        activeShift.driver?.id ? String(activeShift.driver.id) : undefined
                      }
                      activeShiftId={activeShift.id}
                      activeShiftStatus={activeShift.status}
                      planStart={activeShift.twStartAt || activeShift.createdAt}
                      defaultDate={defaultDate}
                      trigger={
                        <IcPencil
                          data-testid="truck-card-edit-shift"
                          className="box-content p-2 w-4 h-4 text-brand-blue cursor-pointer hover:text-brand-blue/80 transition"
                        />
                      }
                    />
                  ))}
                {activeShift && (
                  <IcPrint
                    data-testid="truck-card-print-shift"
                    className={cn(
                      "box-content p-1.5 w-5 h-5 transition",
                      isPlanned || isTerminal
                        ? "text-text-secondary/40 cursor-not-allowed"
                        : "text-brand-blue cursor-pointer hover:text-brand-blue/80",
                    )}
                    onClick={() =>
                      !isPlanned &&
                      !isTerminal &&
                      truckStatus.truck &&
                      on_print_shift(activeShift, truckStatus.truck)
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  },
);

TruckCard.displayName = "TruckCard";
